import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createSubject as createSubjectService,
  cloneSubject as cloneSubjectService,
  updateSubject as updateSubjectService,
  deleteSubject as deleteSubjectService,
  archiveSubject as archiveSubjectService,
  getSubjectsSkipLimit as getSubjectsSkipLimitService,
  getSubjects as getSubjectsService,
  batchDeleteSubjects as batchDeleteSubjectsService,
  batchUpdateSubjectsCurriculumElements as batchUpdateSubjectsCurriculumElementsService,
} from "../../services/subjects/subjects.service.js";
import { handlerErrorV2 } from "../../utils/errors.jsx";
import {
  updateSubjectsList,
  updateSubjectsListMethod,
} from "../../store/actions/subjects.actions";
import { toggleAlertModule } from "../../store/actions/alert.actions";
import { useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

const useSubjects = () => {
  const { curriculumId } = useParams();
  const existLinceseAssessment = useSelector((state) =>
    state?.tenant?.license?.find((license) => license.code === "assessment")
  );
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const getSubjects = useCallback(
    async (curriculumId, skip, rowsPerPage, filter) => {
      if (curriculumId) {
        setIsLoadingList(true);
        try {
          const filterData = {
            name: filter.name,
            code: filter.code,
            curriculumelements: filter.curriculumelements,
          };
          const response = await getSubjectsSkipLimitService(
            curriculumId,
            skip,
            rowsPerPage,
            filterData
          );
          if (!response?.data && response?.error) {
            enqueueSnackbar(response.error.message || "An error ocurred", {
              variant: "error",
            });
          } else {
            dispatch(
              updateSubjectsList(
                curriculumId,
                response?.data || { data: [], count: 0 }
              )
            );
          }
        } catch (error) {
          const message = handlerErrorV2(error);
          enqueueSnackbar(message, { variant: "error" });
        } finally {
          setIsLoadingList(false);
        }
      }
    },
    []
  );

  const getAllSubjects = useCallback(async (curriculumId, filter = {}) => {
    if (curriculumId) {
      setIsLoadingList(true);
      try {
        const filterData = {
          name: filter.name,
          curriculumElements: filter.curriculumElements,
        };
        const response = await getSubjectsService(curriculumId, filterData);
        if (!response?.data && response?.error) {
          enqueueSnackbar(response.error.message || "An error ocurred", {
            variant: "error",
          });
        } else {
          const data = response?.data || [];
          dispatch(updateSubjectsList(null, { data, count: data.length }));
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setIsLoadingList(false);
      }
    }
  }, []);

  const validateForm = (form) => {
    const errors = {};
    if (!form.name) {
      errors.name = "field is required";
    }
    if (!form.code) {
      errors.code = "field is required";
    }
    if (isEmpty(form.curriculumelements) && existLinceseAssessment) {
      errors.curriculumElements = "Curriculum elements is required";
    }

    if (!isEmpty(errors)) {
      return { error: true, ...errors };
    }

    return { error: false };
  };
  const formData = (form) => {
    const formData = {
      _id: form._id,
      name: form.name,
      code: form.code,
      description: form.description,
      curriculumelements: form.curriculumelements,
      curriculumId: form.curriculumId,
    };
    return formData;
  };

  const createSubject = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form);
      const response = await createSubjectService(curriculumId, data);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(updateSubjectsListMethod(curriculumId, "POST", response.data));
        enqueueSnackbar("Subject created successfully", { variant: "success" });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const cloneSubject = async (subjectId) => {
    try {
      setIsLoading(true);
      const response = await cloneSubjectService(curriculumId, subjectId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(updateSubjectsListMethod(curriculumId, "POST", response.data));
        enqueueSnackbar("Subject cloned successfully", { variant: "success" });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubject = async (subjectId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result;
    }

    try {
      const data = formData(form);
      const response = await updateSubjectService(
        curriculumId,
        subjectId,
        data
      );
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(updateSubjectsListMethod(curriculumId, "PUT", response.data));
        enqueueSnackbar("Subject updated successfully", { variant: "success" });
        dispatch(toggleAlertModule());

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const deleteSubject = async (subjectId) => {
    try {
      setIsLoading(true);
      const response = await deleteSubjectService(curriculumId, subjectId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(updateSubjectsListMethod(curriculumId, "DELETE", subjectId));
        enqueueSnackbar("Subject deleted successfully", { variant: "success" });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const archiveSubject = async (subjectId) => {
    try {
      setIsLoading(true);
      const response = await archiveSubjectService(curriculumId, subjectId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(updateSubjectsListMethod(curriculumId, "DELETE", subjectId));
        enqueueSnackbar("Subject archived successfully", {
          variant: "success",
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const batchDeleteSubjects = async (subjectIds = []) => {
    try {
      setIsLoading(true);
      const response = await batchDeleteSubjectsService(curriculumId, subjectIds);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(
          updateSubjectsListMethod(curriculumId, "BATCH_DELETE", subjectIds)
        );
        enqueueSnackbar("Subjects deleted successfully", {
          variant: "success",
        });

        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  const batchUpdateSubjectsCurriculumElements = async (subjectIds = [], form = {}) => {
    try {
      setIsLoading(true);
      const response = await batchUpdateSubjectsCurriculumElementsService(curriculumId, subjectIds, form);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || "An error ocurred",
        };
      } else {
        dispatch(
          updateSubjectsListMethod(curriculumId, "BATCH_PUT", response.data)
        );
        enqueueSnackbar("Subjects updated successfully", {
          variant: "success",
        });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoadingList,
    isLoading,
    getSubjects,
    getAllSubjects,
    createSubject,
    cloneSubject,
    updateSubject,
    deleteSubject,
    archiveSubject,
    batchDeleteSubjects,
    batchUpdateSubjectsCurriculumElements
  };
};

export default useSubjects;
